import { useEffect, useRef, useState } from "react"

import { useReducedMotion } from "motion/react"

import { PRISM_GUIDING_MS } from "@/features/chat/components/prism-retrieval/prism-retrieval-constants"

import { getPrismTurnMemory, usePrismTurnKey } from "./use-prism-turn"

export type PrismSequence = {
  /**
   * The view mounted during its live turn, whether or not motion is allowed.
   * A live view reserves its final structure from mount on this, so the
   * answer below never moves, with reduced motion too.
   */
  mountedLive: boolean
  /**
   * Motion classes may be applied: the view mounted during the live turn and
   * the user has not asked for reduced motion. It stays true after the turn
   * ends, so the live turn can settle into its final state on the brand curve.
   */
  motion: boolean
  /** The timed sequence is running (motion, and the turn is still live). */
  running: boolean
  /** Steps reached so far; equals `stepTimesMs.length` whenever not running. */
  step: number
  /**
   * Entrance animations may play: `motion`, and this mount did not take over
   * a sequence that had already finished before the message remounted. What
   * was already on screen then must not enter a second time.
   */
  entrance: boolean
}

/**
 * Drives a Prism view's live sequence (#3261). Only a view that mounts while
 * its turn is live ever animates; one that mounts on a finished or older turn
 * renders its final state at once, with no replay on scroll or re-render.
 * With `prefers-reduced-motion`, every view renders its final state at once.
 *
 * Steps pace with the data: a step is reached no earlier than its time in
 * `stepTimesMs`, never beyond `cap` (the steps FILTH has backed so far), and
 * never within one Guiding beat of the step before it. So the last backed
 * step stays active until more data arrives, and the steps it then unlocks
 * complete one after another instead of in one frame.
 *
 * With a `memoryId` inside a Prism turn (`PrismTurnKeyContext`), a live
 * sequence leaves its progress in the turn's memory, and a remount of the
 * same live turn resumes from it rather than replaying the steps.
 */
export function usePrismSequence(
  isLive: boolean,
  stepTimesMs: readonly number[],
  {
    cap = stepTimesMs.length,
    memoryId,
  }: {
    /** Steps FILTH has backed so far. */
    cap?: number
    /** Names this sequence in its Prism turn's memory. */
    memoryId?: string
  } = {}
): PrismSequence {
  const reduceMotion = useReducedMotion() === true
  const turnKey = usePrismTurnKey()
  const memoryPrefix = turnKey !== undefined && memoryId ? `${memoryId}:` : undefined
  // Captured once: whether this view first appeared during its live turn, and
  // the progress an earlier mount of the same live turn left behind.
  const [initial] = useState(() => {
    const values = memoryPrefix ? getPrismTurnMemory(turnKey)?.values : undefined
    const step = isLive ? values?.get(`${memoryPrefix}step`) : undefined
    if (!values || step === undefined) return { mountedLive: isLive, step: 0, clock: null }
    const start = values.get(`${memoryPrefix}start`)
    const last = values.get(`${memoryPrefix}last`) ?? Number.NEGATIVE_INFINITY
    return {
      mountedLive: true,
      step,
      clock: start === undefined ? null : { start, last },
    }
  })
  const mountedLive = initial.mountedLive
  const motion = mountedLive && !reduceMotion
  const running = motion && isLive
  const [step, setStep] = useState(initial.step)
  const clock = useRef<{ start: number; last: number } | null>(initial.clock)
  const target = Math.min(cap, stepTimesMs.length)

  useEffect(() => {
    if (!running || step >= target) return
    const now = Date.now()
    clock.current ??= { start: now, last: Number.NEGATIVE_INFINITY }
    const { start, last } = clock.current
    const delay = Math.max(0, start + stepTimesMs[step] - now, last + PRISM_GUIDING_MS - now)
    const timer = setTimeout(() => {
      if (clock.current) clock.current.last = Date.now()
      setStep(step + 1)
    }, delay)
    return () => clearTimeout(timer)
    // Step times are module constants; `running`, `step` and `target` pace it.
  }, [running, step, target])

  // Leave the progress behind for a remount of the same live turn.
  useEffect(() => {
    if (!running || !memoryPrefix) return
    const values = getPrismTurnMemory(turnKey)?.values
    if (!values) return
    values.set(`${memoryPrefix}step`, step)
    if (clock.current) {
      values.set(`${memoryPrefix}start`, clock.current.start)
      values.set(`${memoryPrefix}last`, clock.current.last)
    }
  }, [running, memoryPrefix, turnKey, step])

  return {
    mountedLive,
    motion,
    running,
    step: running ? step : stepTimesMs.length,
    entrance: motion && initial.step < stepTimesMs.length,
  }
}
